// TanStack Query hooks for the Subsidy Matching tab (Slice G) and the
// review-queue "Mark as subsidy payment" dropdown (Slice F manual fallback).
//
// Query-key factories follow the existing reviewQueueKeys pattern so cache
// invalidation stays predictable.

import { useMemo, useRef } from "react";
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { ReconciliationService } from "@/api";
import type {
  reconciliation_ApplySubsidyPaymentRequest,
  reconciliation_ApplySubsidyPaymentResponse,
  reconciliation_CreditedSubsidyPaymentListResponse,
  reconciliation_IgnoredSubsidyPaymentListResponse,
  reconciliation_MarkAsSubsidyRequest,
  reconciliation_MarkAsSubsidyResponse,
  reconciliation_PendingSubsidyPaymentDTO,
  reconciliation_PendingSubsidyPaymentListResponse,
  reconciliation_SubsidyObligationDTO,
  reconciliation_SubsidyObligationListResponse,
} from "@/api";
import { invalidateMoneyViews } from "./invalidateMoneyViews";
import { subsidyOfficeReportKeys } from "./useSubsidyOfficeReports";

/**
 * subsidyMatchingKeys is the query-key factory for the subsidy matching tab.
 * `all` is invalidated by every mutation (mark-as-subsidy, apply) so the
 * left rail and the right pane both refetch. The list keys carry the active
 * orgId (tenant cache isolation — frontend.md: tenant-scoped query keys MUST
 * include the active orgId) plus the limit/offset pagination args the hooks
 * forward to the API, so cache entries never collide across tenants or pages.
 */
export const subsidyMatchingKeys = {
  all: ["reconciliation", "subsidy-matching"] as const,
  payments: (
    orgId: string | null,
    officeId: string | null,
    search: string,
    limit?: number,
    offset?: number,
  ) =>
    [
      ...subsidyMatchingKeys.all,
      "payments",
      orgId,
      officeId,
      search,
      limit,
      offset,
    ] as const,
  // queue keys carry the Active/Ignored/Credited status so the three tabs cache
  // independently (issue #761, Phase 3a).
  queue: (
    orgId: string | null,
    status: string,
    officeId: string | null,
    search: string,
    limit?: number,
    offset?: number,
  ) =>
    [
      ...subsidyMatchingKeys.all,
      "queue",
      orgId,
      status,
      officeId,
      search,
      limit,
      offset,
    ] as const,
  obligations: (
    orgId: string | null,
    debtorAccountId: string | null,
    period: { year: number; month: number } | null,
    includePaid: boolean,
    status: SubsidyObligationStatus,
    childInternalId: string,
    limit?: number,
    offset?: number,
  ) =>
    [
      ...subsidyMatchingKeys.all,
      "obligations",
      orgId,
      debtorAccountId,
      period,
      includePaid,
      status,
      childInternalId,
      limit,
      offset,
    ] as const,
};

export interface UseSubsidyPaymentsListParams {
  officeId?: string | null;
  /**
   * Optional search substring forwarded to the API. Matches the QR-reference,
   * the bank-transaction remittance_info (a.k.a. "payment reference") and
   * the displayed amount, mirroring the review-queue search.
   */
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * Lists routed-but-unapplied subsidy payments. The left rail of the Subsidy
 * Matching tab consumes this hook. Empty `officeId` returns rows across all
 * subsidy offices (operator filter dropdown).
 */
export function useSubsidyPaymentsList(
  params: UseSubsidyPaymentsListParams = {},
) {
  const { orgId } = useAuth();
  const officeId = params.officeId ?? null;
  const search = params.search ?? "";
  return useQuery<reconciliation_PendingSubsidyPaymentListResponse>({
    queryKey: subsidyMatchingKeys.payments(
      orgId ?? null,
      officeId,
      search,
      params.limit,
      params.offset,
    ),
    // The endpoint now takes a leading `status` param (issue #761). The
    // Active tab explicitly asks for "active". The generated method is typed
    // to the credited envelope (swagger keeps the last @Success) but the wire
    // shape for active is the pending envelope — narrow it here.
    queryFn: async () => {
      const res =
        await ReconciliationService.getApiV1ReconciliationSubsidyPayments(
          "active",
          officeId ?? undefined,
          search === "" ? undefined : search,
          params.limit,
          params.offset,
        );
      return res as unknown as reconciliation_PendingSubsidyPaymentListResponse;
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // FRESHNESS POLICY (see useDebtorAccountsList): the app-wide default is
    // `refetchOnWindowFocus: false`, and every money query an operator ACTS on
    // opts back in here. Correcting an invoice in a second browser tab is the
    // documented workflow on this screen, and a cache invalidation only reaches
    // the tab that issued the write. Without this opt-in the 30s staleTime is a
    // floor with nothing to end it, so coming back to this tab showed the
    // pre-correction Expected figure until a manual reload.
    refetchOnWindowFocus: true,
  });
}

/** The backend's page cap for the subsidy payments list. */
const OPEN_OFFICE_PAYMENTS_PAGE_LIMIT = 200;

/**
 * Lists EVERY open subsidy payment of one office, unsearched and fully paged.
 * The reclaim netting (issue #1576) offers its source payments from this list:
 * reading them off the rail query would drop every payment the rail search
 * hides or a page leaves out, and the operator could not net from it.
 */
export function useAllOpenSubsidyOfficePayments(officeId: string | null) {
  const { orgId } = useAuth();
  return useQuery<reconciliation_PendingSubsidyPaymentDTO[]>({
    queryKey: [
      ...subsidyMatchingKeys.all,
      "open-office-payments",
      orgId ?? null,
      officeId,
    ] as const,
    queryFn: async () => {
      const items: reconciliation_PendingSubsidyPaymentDTO[] = [];
      for (let offset = 0; ; offset += OPEN_OFFICE_PAYMENTS_PAGE_LIMIT) {
        const page =
          (await ReconciliationService.getApiV1ReconciliationSubsidyPayments(
            "active",
            officeId ?? undefined,
            undefined,
            OPEN_OFFICE_PAYMENTS_PAGE_LIMIT,
            offset,
          )) as unknown as reconciliation_PendingSubsidyPaymentListResponse;
        const rows = page.items ?? [];
        items.push(...rows);
        if (
          rows.length === 0 ||
          rows.length < OPEN_OFFICE_PAYMENTS_PAGE_LIMIT ||
          items.length >= (page.total ?? 0)
        ) {
          return items;
        }
      }
    },
    enabled: !!orgId && !!officeId,
    staleTime: 30_000,
    // Same freshness policy as the rail: the operator acts on these amounts.
    refetchOnWindowFocus: true,
  });
}

/** The three subsidy office-payment queue tabs (issue #761, Phase 3a). */
export type SubsidyOfficeQueueStatus = "active" | "ignored" | "credited";

export interface UseSubsidyOfficePaymentQueueParams {
  status: SubsidyOfficeQueueStatus;
  officeId?: string | null;
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * Lists subsidy office payments for the requested queue tab (Active / Ignored /
 * Credited), mirroring the parent Review Queue's per-status pattern. The
 * generated client method is typed to the credited envelope (swagger keeps the
 * last @Success); callers narrow `items` to the DTO that matches `status`.
 */
export function useSubsidyOfficePaymentQueue(
  params: UseSubsidyOfficePaymentQueueParams,
) {
  const { orgId } = useAuth();
  const officeId = params.officeId ?? null;
  const search = params.search ?? "";
  return useQuery<
    | reconciliation_PendingSubsidyPaymentListResponse
    | reconciliation_IgnoredSubsidyPaymentListResponse
    | reconciliation_CreditedSubsidyPaymentListResponse
  >({
    queryKey: subsidyMatchingKeys.queue(
      orgId ?? null,
      params.status,
      officeId,
      search,
      params.limit,
      params.offset,
    ),
    queryFn: async () => {
      const res =
        await ReconciliationService.getApiV1ReconciliationSubsidyPayments(
          params.status,
          officeId ?? undefined,
          search === "" ? undefined : search,
          params.limit,
          params.offset,
        );
      return res as unknown as reconciliation_CreditedSubsidyPaymentListResponse;
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // FRESHNESS POLICY (see useDebtorAccountsList): the app-wide default is
    // `refetchOnWindowFocus: false`, and every money query an operator ACTS on
    // opts back in here. Correcting an invoice in a second browser tab is the
    // documented workflow on this screen, and a cache invalidation only reaches
    // the tab that issued the write. Without this opt-in the 30s staleTime is a
    // floor with nothing to end it, so coming back to this tab showed the
    // pre-correction Expected figure until a manual reload.
    refetchOnWindowFocus: true,
  });
}

/**
 * Server-side triage filter over the derived balance. Allowlisted by the
 * backend (400 on anything else), mirroring the subsidy ledger's filter so
 * "outstanding" means the same on both surfaces.
 */
export type SubsidyObligationStatus = "all" | "outstanding" | "paid";

export interface UseSubsidyObligationsListParams {
  debtorAccountId: string | null;
  /** Calendar year (4-digit). When null, the query is disabled. */
  periodYear: number | null;
  /** Calendar month, 1–12. When null, the query is disabled. */
  periodMonth: number | null;
  /** Default true on the matching tab — surfaces paid rows as read-only. */
  includePaid?: boolean;
  /** Row filter; defaults to "all". Never affects the response's monthTotals. */
  status?: SubsidyObligationStatus;
  /**
   * Narrows the month to one child by internal id ("" / undefined = whole
   * month). Server-side: the list is page-capped at 500, so filtering the
   * fetched page instead would silently drop the child on a large month.
   */
  childInternalId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Lists subsidy obligations (invoice_splits with split_kind='subsidy') for
 * the given debtor in the selected calendar month. When includePaid is
 * true (default) already-paid rows are returned so the FE can render the
 * full month picture with allocation disabled on paid rows. Returns
 * empty/disabled when debtorAccountId or year/month is null.
 *
 * The matching tab moved to `useSubsidyObligationsForMonths` in issue #1466 and
 * passes a one-entry list for the single-month case, so this hook has no caller
 * in the app today. It stays as the single-month contract of the endpoint and
 * as the pin on the query-key factory and the positional argument order that
 * the multi-month hook copies; its tests are what would catch a drift there.
 */
export function useSubsidyObligationsList(
  params: UseSubsidyObligationsListParams,
) {
  const { orgId } = useAuth();
  const includePaid = params.includePaid ?? true;
  const status = params.status ?? "all";
  const childInternalId = params.childInternalId ?? "";
  const enabled =
    params.debtorAccountId !== null &&
    params.debtorAccountId !== "" &&
    params.periodYear !== null &&
    params.periodMonth !== null;
  return useQuery<reconciliation_SubsidyObligationListResponse>({
    queryKey: subsidyMatchingKeys.obligations(
      orgId ?? null,
      params.debtorAccountId,
      enabled
        ? {
            year: params.periodYear as number,
            month: params.periodMonth as number,
          }
        : null,
      includePaid,
      status,
      childInternalId,
      params.limit,
      params.offset,
    ),
    queryFn: () =>
      // Positional args, in the generated order:
      // (debtorAccountId, periodYear, periodMonth, includePaid, status,
      //  childInternalId, periodStart, periodEnd, limit, offset). The two
      //  `undefined`s are the deprecated period_start/period_end pair — keep
      //  them, or limit/offset silently slide into the wrong query params.
      ReconciliationService.getApiV1ReconciliationSubsidyObligations(
        params.debtorAccountId as string,
        params.periodYear ?? undefined,
        params.periodMonth ?? undefined,
        includePaid,
        status,
        childInternalId === "" ? undefined : childInternalId,
        undefined,
        undefined,
        params.limit,
        params.offset,
      ),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // FRESHNESS POLICY (see useDebtorAccountsList): the app-wide default is
    // `refetchOnWindowFocus: false`, and every money query an operator ACTS on
    // opts back in here. Correcting an invoice in a second browser tab is the
    // documented workflow on this screen, and a cache invalidation only reaches
    // the tab that issued the write. Without this opt-in the 30s staleTime is a
    // floor with nothing to end it, so coming back to this tab showed the
    // pre-correction Expected figure until a manual reload.
    refetchOnWindowFocus: true,
  });
}

export interface UseSubsidyObligationsForMonthsParams extends Omit<
  UseSubsidyObligationsListParams,
  "periodYear" | "periodMonth"
> {
  /** One entry per Bezugsmonat to load. Empty disables every query. */
  months: Array<{ year: number; month: number }>;
  /** Load every page before applying client-side filters and totals. */
  allPages?: boolean;
}

/** Element-wise identity comparison of two per-month data lists. */
function sameMonthData(
  a: (reconciliation_SubsidyObligationListResponse | undefined)[],
  b: (reconciliation_SubsidyObligationListResponse | undefined)[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * useSubsidyObligationsForMonths loads the obligations of SEVERAL Bezugsmonate
 * at once, so one apply can settle a tranche that pays three months for one
 * child (issue #1466).
 *
 * It is `useQueries`, not `useSubsidyObligationsList` called in a loop: the
 * months list changes length between renders and a hook call inside a `.map`
 * breaks the rules of hooks. The key factory, the query function's positional
 * argument order and the freshness settings are the single-month hook's, so the
 * two share one cache entry per month and one invalidation.
 *
 * `items` concatenates the months in the order they were given. `isSettled` is
 * true only when every query is a settled, non-placeholder success, which is
 * what a caller must gate derived fan-out on.
 */
export function useSubsidyObligationsForMonths(
  params: UseSubsidyObligationsForMonthsParams,
) {
  const { orgId } = useAuth();
  const includePaid = params.includePaid ?? true;
  const status = params.status ?? "all";
  const childInternalId = params.childInternalId ?? "";
  const enabled =
    params.debtorAccountId !== null && params.debtorAccountId !== "";

  const monthResults = useQueries({
    queries: params.months.map((month) => ({
      queryKey: [
        ...subsidyMatchingKeys.obligations(
          orgId ?? null,
          params.debtorAccountId,
          enabled ? { year: month.year, month: month.month } : null,
          includePaid,
          status,
          childInternalId,
          params.limit,
          params.offset,
        ),
        ...(params.allPages ? ["all-pages"] : []),
      ],
      queryFn: async () => {
        const items: reconciliation_SubsidyObligationDTO[] = [];
        let first: reconciliation_SubsidyObligationListResponse | undefined;
        for (;;) {
          const response =
            await ReconciliationService.getApiV1ReconciliationSubsidyObligations(
              params.debtorAccountId as string,
              month.year,
              month.month,
              includePaid,
              status,
              childInternalId || undefined,
              undefined,
              undefined,
              params.limit,
              params.allPages
                ? (params.offset ?? 0) + items.length
                : params.offset,
            );
          first ??= response;
          if (!params.allPages) return response;
          const page = response.items ?? [];
          if (!page.length && items.length < (response.total ?? 0))
            throw new Error("Incomplete subsidy obligations");
          const existing = new Set(items.map((item) => item.splitId));
          if (page.some((item) => existing.has(item.splitId)))
            throw new Error("Repeated subsidy obligations page");
          items.push(...page);
          if (!page.length || items.length >= (response.total ?? items.length))
            break;
        }
        return { ...first, items };
      },
      enabled,
      placeholderData: keepPreviousData,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    })),
  });

  // `items` MUST keep its identity while the months' data does. `useQueries`
  // hands back a fresh results array on every render, so a bare flatMap would
  // produce a new array each time; the tab feeds `items` into the overlay memo
  // and into the prefill effect, which compares by identity and calls
  // setAllocations. A per-render identity there is an infinite render loop, not
  // a wasted memo.
  //
  // The per-month data list is stabilised by hand rather than spread into a
  // dependency array: React compares only the common prefix of two dependency
  // lists of different length, so a spread list misses the case where the
  // months list grows or shrinks while the leading months' data keeps its
  // identity (select a tranche while the picker sits on its first month, or
  // switch from the tranche to a payment without a statement). The list below
  // changes identity exactly when a month is added, removed, or refetched.
  const monthData = monthResults.map((result) => result.data);
  const stableMonthDataRef = useRef<
    (reconciliation_SubsidyObligationListResponse | undefined)[]
  >([]);
  if (!sameMonthData(stableMonthDataRef.current, monthData)) {
    stableMonthDataRef.current = monthData;
  }
  const stableMonthData = stableMonthDataRef.current;
  const freshItems = useMemo(
    () => stableMonthData.flatMap((data) => data?.items ?? []),
    [stableMonthData],
  );

  // `keepPreviousData` does NOT survive a key change here. `useQueries` builds a
  // fresh observer per query key, so on a month or filter switch every result
  // goes back to pending with no data - where the single-month `useQuery` would
  // have served the previous rows. Without a carry the allocation grid unmounts
  // mid-refetch and the operator loses the rows AND the busy affordance that
  // says why. The last non-pending list is therefore held here and served while
  // anything is in flight; `isSettled` stays false throughout, so no derived
  // figure is computed from carried rows.
  const carriedItemsRef = useRef<reconciliation_SubsidyObligationDTO[]>([]);
  const isPending = monthResults.some((result) => result.isPending);
  if (!isPending) carriedItemsRef.current = freshItems;
  const items = isPending ? carriedItemsRef.current : freshItems;

  return {
    items,
    monthResults,
    // Mirrors keepPreviousData: a refetch that still has rows to show is not a
    // load. Only a first fetch with nothing carried blanks the grid.
    isLoading:
      monthResults.some((result) => result.isLoading) && items.length === 0,
    isFetching: monthResults.some((result) => result.isFetching),
    isSettled:
      monthResults.length > 0 &&
      monthResults.every(
        (result) => result.isSuccess && !result.isPlaceholderData,
      ),
  };
}

export interface ApplySubsidyPaymentInput {
  paymentId: string;
  body: reconciliation_ApplySubsidyPaymentRequest;
}

/**
 * Apply a routed subsidy payment to a list of subsidy splits, optionally
 * crediting the remainder to the office's debtor account. Invalidates the
 * subsidy matching cache + downstream caches that may be affected by the
 * ledger writes (debtor accounts, payment applications, review queue, and
 * the per-child subsidy ledger shown in the debtor-detail slide-over).
 */
export function useApplySubsidyPayment() {
  const queryClient = useQueryClient();
  return useMutation<
    reconciliation_ApplySubsidyPaymentResponse,
    unknown,
    ApplySubsidyPaymentInput
  >({
    mutationFn: ({ paymentId, body }) =>
      ReconciliationService.postApiV1ReconciliationSubsidyPaymentsApply(
        paymentId,
        body,
      ),
    onSuccess: async () => {
      // Every cache that can show this money: the subsidy rail and obligations
      // grid, the review queue, the per-child ledger and totals, the invoice
      // list, the debtor account statement and balance charts. All of it lives
      // in invalidateMoneyViews' one list — hand-picking a subset here is what
      // let the picks drift, and it broadcasts to the operator's other tabs,
      // which a local invalidateQueries never does.
      await invalidateMoneyViews(queryClient);
      // Not money, so not in that list: an apply can move a payment off the
      // rail, and the office statement's candidate list and link state are
      // derived from that payment.
      queryClient.invalidateQueries({
        queryKey: subsidyOfficeReportKeys.all,
      });
    },
  });
}

export interface MarkAsSubsidyInput {
  paymentId: string;
  body: reconciliation_MarkAsSubsidyRequest;
}

/**
 * Manually flag a payment as a subsidy payment (Slice F manual fallback).
 * Invalidates the review queue (the payment leaves it) and the subsidy
 * matching cache (the payment lands there).
 */
export function useMarkAsSubsidy() {
  const queryClient = useQueryClient();
  return useMutation<
    reconciliation_MarkAsSubsidyResponse,
    unknown,
    MarkAsSubsidyInput
  >({
    mutationFn: ({ paymentId, body }) =>
      ReconciliationService.postApiV1ReconciliationPaymentsMarkAsSubsidy(
        paymentId,
        body,
      ),
    onSuccess: async () => {
      // The payment leaves the review queue and lands on the subsidy rail, and
      // routing it onto an office debtor moves that office's ledger and
      // per-child cash-in totals. All four namespaces are in the one list.
      await invalidateMoneyViews(queryClient);
    },
  });
}
